import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ScanLine, LayoutDashboard, LogOut, Settings as SettingsIcon, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

function NavItems({ loc, prefix, onNavigate }) {
  const cls = (active) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${active ? "bg-[#1E1E1E] text-white" : "text-muted-foreground hover:text-white hover:bg-[#161616]"}`;
  return (
    <nav className="space-y-1">
      <Link to="/dashboard" data-testid={`${prefix}nav-dashboard`} onClick={onNavigate}>
        <div className={cls(loc.pathname === "/dashboard")}><LayoutDashboard className="w-4 h-4" /> Projects</div>
      </Link>
      <Link to="/settings" data-testid={`${prefix}nav-settings`} onClick={onNavigate}>
        <div className={cls(loc.pathname === "/settings")}><SettingsIcon className="w-4 h-4" /> Settings</div>
      </Link>
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const Brand = (
    <div className="flex items-center gap-2">
      <img src="/zentara-logo.png" alt="Zentara Integrity" className="w-7 h-7 object-contain" />
      <span className="font-display font-bold text-lg">Zentara <span className="text-[#C5A24A]">Integrity</span></span>
    </div>
  );

  const UserFooter = (
    <div className="p-3 border-t border-white/10">
      <div className="px-3 py-2 mb-1">
        <p className="text-sm font-medium truncate">{user?.name || "Inspector"}</p>
        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
      </div>
      <Button variant="ghost" data-testid="logout-button" onClick={logout} className="w-full justify-start gap-3 text-muted-foreground hover:text-white h-9">
        <LogOut className="w-4 h-4" /> Sign out
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground grain">
      {/* Desktop sidebar */}
      <aside className="w-60 shrink-0 border-r border-white/10 bg-[#0A0A0A] hidden md:flex flex-col fixed h-screen z-20">
        <div className="h-16 flex items-center px-6 border-b border-white/10">{Brand}</div>
        <div className="flex-1 p-3"><NavItems loc={loc} prefix="" /></div>
        {UserFooter}
      </aside>

      <main className="flex-1 md:ml-60 relative z-10">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-40 h-14 flex items-center justify-between px-4 border-b border-white/10 bg-[#0A0A0A]">
          {Brand}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button" className="text-muted-foreground hover:text-white">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#0A0A0A] border-white/10 p-0 w-72 flex flex-col">
              <div className="h-14 flex items-center px-6 border-b border-white/10">{Brand}</div>
              <div className="flex-1 p-3"><NavItems loc={loc} prefix="mobile-" onNavigate={() => setOpen(false)} /></div>
              {UserFooter}
            </SheetContent>
          </Sheet>
        </div>
        {children}
      </main>
    </div>
  );
}
